import React, { useState, useEffect, useCallback } from 'react';
import Input from 'antd/es/input';
import Button from 'antd/es/button';
import Table from 'antd/es/table';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import message from 'antd/es/message';
import { SearchOutlined } from '@ant-design/icons';
import Card from 'antd/es/card';
import Space from 'antd/es/space';
import Typography from 'antd/es/typography';
import { studentApi } from '../../services/api';
import { Student, SearchStudentsResponse } from '../../types/student';

const { Title } = Typography;

interface StudentSearchProps {
  initialSearch?: string;
}

const StudentSearch: React.FC<StudentSearchProps> = ({ initialSearch = '' }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchStudentsResponse>({
    data: [],
    meta: { total: 0, page: 1, last_page: 1 },
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  useEffect(() => {
    setSearchTerm(initialSearch);
  }, [initialSearch]);

  const searchStudents = useCallback(async (
    page: number = 1,
    search: string = searchTerm,
    pageSize: number = pagination.pageSize
  ) => {
    if (!search.trim()) {
      setSearchResults({ data: [], meta: { total: 0, page: 1, last_page: 1 } });
      setPagination((prev) => ({ ...prev, current: 1 }));
      return;
    }

    setLoading(true);
    try {
      const results = await studentApi.searchStudents(
        search,
        page,
        pageSize
      );
      setSearchResults(results);
      setPagination((prev) => ({
        ...prev,
        current: results.meta.page,
        pageSize,
      }));
    } catch (error) {
      console.error('Error searching students:', error);
      message.error('Failed to search students');
    } finally {
      setLoading(false);
    }
  }, [pagination.pageSize, searchTerm]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setPagination((prev) => ({ ...prev, current: 1 }));
      searchStudents(1, searchTerm, pagination.pageSize);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchTerm, searchStudents, pagination.pageSize]);

  const handleTableChange = (nextPagination: TablePaginationConfig) => {
    const nextPage = nextPagination.current ?? 1;
    const nextPageSize = nextPagination.pageSize ?? pagination.pageSize;
    setPagination((prev) => ({
      ...prev,
      current: nextPage,
      pageSize: nextPageSize,
    }));
    searchStudents(nextPage, searchTerm, nextPageSize);
  };

  const columns: ColumnsType<Student> = [
    {
      title: 'Name',
      dataIndex: ['user', 'firstName'],
      key: 'name',
      render: (_: any, record: Student) => (
        <span>{`${record.user.firstName} ${record.user.lastName}`}</span>
      ),
    },
    {
      title: 'Email',
      dataIndex: ['user', 'email'],
      key: 'email',
    },
    {
      title: 'Grade',
      dataIndex: 'grade',
      key: 'grade',
    },
  ];

  return (
    <Card>
      <Title level={4}>Search Students</Title>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <Space>
          <Input
            placeholder="Search by name, email, or grade"
            prefix={<SearchOutlined />}
            value={searchTerm}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSearchTerm(e.target.value)
            }
            style={{ width: 300 }}
            allowClear={true}
          />
          <Button 
            type="primary" 
            onClick={() => searchStudents(1)} 
            loading={loading}
          >
            Search
          </Button>
        </Space>

        <Table
          columns={columns}
          dataSource={searchResults.data}
          rowKey={(record) => String(record.id)}
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: searchResults.meta.total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: (total: number) => `Total ${total} students`,
          }}
          onChange={handleTableChange}
        />
      </Space>
    </Card>
  );
};

export default StudentSearch;
